/**
 * Report filter state with a removable default reporting window.
 *
 * Reports open on the last 30 days so the first load is fast, but "all time" is
 * simply the *absence* of a date filter — clear the range and the report pulls
 * everything (the backend runs the query with no lower date bound). There is no
 * "All time" option in the picker; removing the filter is how you get all data.
 *
 * A collection's `useFilterState` store is shared by its records page and its
 * reports, and can't tell "never chosen" from "cleared" by value alone, so we
 * distinguish three states by the shape of `dateRange`:
 *
 *   - `undefined`  → never chosen. We inject the 30-day default for display +
 *                    fetch, but do NOT persist it: a default is not a choice,
 *                    and persisting it would open this collection's records
 *                    page on a 30-day filter nobody selected.
 *   - `{}`         → the user explicitly cleared the range. Persisted as an
 *                    empty object (and a `dateRangeCleared` URL sentinel) so it
 *                    survives reloads/deep links and reads as "all time / no
 *                    filter"; the backend applies no date bound.
 *   - real range   → an explicit preset/custom selection. Persisted as-is.
 *
 * Only `filters`, `setFilters` and `reset` are exposed so no caller can bypass
 * this normalization.
 */
import { useCallback, useMemo } from 'react';

import {
  useFilterState,
  type FilterConfig,
  type DateRangeFilterConfig,
  type UseFilterStateOptions,
} from '../records/filter';

const DEFAULT_REPORT_DATE_RANGE: DateRangeFilterConfig = { preset: 'last_30_days' };

/** Explicit "all time / no filter": present but empty (user cleared the range). */
const ALL_TIME_DATE_RANGE: DateRangeFilterConfig = {};

/** True for a bare last-30-days preset (the shape of the injected default). */
function isSyntheticDefault(dateRange?: DateRangeFilterConfig): boolean {
  return dateRange?.preset === 'last_30_days' && !dateRange.from && !dateRange.to;
}

export function useReportFilterState(scope: string, options?: UseFilterStateOptions) {
  const { filters: rawFilters, setFilters: setRawFilters } = useFilterState(scope, options);

  const filters = useMemo<FilterConfig>(
    () =>
      // `undefined` (never chosen) → show/fetch the default window. An empty
      // object (cleared → all time) or a real range is used exactly as stored.
      rawFilters.dateRange === undefined
        ? { ...rawFilters, dateRange: DEFAULT_REPORT_DATE_RANGE }
        : rawFilters,
    [rawFilters],
  );

  const setFilters = useCallback(
    (next: FilterConfig) => {
      if (next.dateRange === undefined) {
        // The user cleared the date filter. Persist an explicit empty range so
        // it means "all time / no filter" and sticks — distinct from
        // `undefined`, which we treat as "never chosen → default window".
        setRawFilters({ ...next, dateRange: ALL_TIME_DATE_RANGE });
        return;
      }

      // The injected default can only be present when the store has no range of
      // its own, so a synthetic range there is ours (the user changed some other
      // filter while the default was showing) — don't persist it, keep the
      // stored date empty: a default is not a choice, and this collection's
      // records page must not inherit a window nobody selected.
      //
      // Accepted limitation: if the store has no range and the user *re-selects*
      // "Last 30 days" (the value already displayed), that no-op selection looks
      // identical to the injected default and is not persisted. The report is
      // unaffected (it re-derives the same default); only cross-propagation of a
      // no-op to the records page is lost.
      if (rawFilters.dateRange === undefined && isSyntheticDefault(next.dateRange)) {
        setRawFilters({ ...next, dateRange: undefined });
        return;
      }

      setRawFilters(next);
    },
    [setRawFilters, rawFilters.dateRange],
  );

  // Reset clears every filter. The date must land on the explicit "all time"
  // state (`{}`) rather than the raw reset's absence (`undefined`): the display
  // memo refills `undefined` with the 30-day default, which would leave the date
  // reading "Last 30 days" while the dimensions cleared — the exact mismatch a
  // reset is meant to avoid. Persisting `{}` also drops the dimensions and
  // writes the `dateRangeCleared` URL sentinel, so the cleared range survives a
  // reload the same way a user-cleared range does.
  const reset = useCallback(() => {
    setRawFilters({ dateRange: ALL_TIME_DATE_RANGE });
  }, [setRawFilters]);

  return { filters, setFilters, reset };
}
